// Forms/Import DSL — client-side expression evaluator (Phase 2c).
//
// The backend Plan carries reactive props (visible/enabled/computed/conditions)
// as serialized expression ASTs — the SAME tree the Go validator built, so the
// two never disagree (see internal/formsdsl/plan.go exprJSON). This evaluates one
// such tree against a `scope` (an object mapping binding names → current values).
//
// Value model: a select/table binding's scope value is its picked ROW object (so
// `cust.Alamat` works and `cust is set` is truthy when a row is chosen); a grid's
// is `{ rows, added, edited, removed }`; scalar widgets are primitives. SQL binds
// and async defaults (sql.scalar) are handled by the runtime, NOT here — this is
// the synchronous, side-effect-free reactive layer.

export function evalExpr(node, scope) {
  if (node == null) return null;
  switch (node.k) {
    case 'str': return node.v;
    case 'num': return Number(node.v);
    case 'bool': return node.v;
    case 'null': return null;
    case 'id': return scope[node.name];
    case 'member': {
      const x = evalExpr(node.x, scope);
      return x == null ? null : x[node.field];
    }
    case 'list': return node.items.map((i) => evalExpr(i, scope));
    case 'obj': {
      const o = {};
      for (const k in node.pairs) o[k] = evalExpr(node.pairs[k], scope);
      return o;
    }
    case 'un':
      if (node.op === 'not') return !truthy(evalExpr(node.x, scope));
      if (node.op === '-') return -Number(evalExpr(node.x, scope));
      return null;
    case 'bin': return evalBin(node, scope);
    case 'is': {
      const s = isSet(evalExpr(node.x, scope));
      return node.test === 'set' ? s : !s;
    }
    case 'in': {
      const v = evalExpr(node.x, scope);
      const list = evalExpr(node.list, scope);
      return Array.isArray(list) && list.some((e) => eq(e, v));
    }
    case 'call': return evalCall(node, scope);
    default: return null;
  }
}

function evalBin(node, scope) {
  const op = node.op;
  // Short-circuit boolean ops.
  if (op === 'and') return truthy(evalExpr(node.l, scope)) && truthy(evalExpr(node.r, scope));
  if (op === 'or') return truthy(evalExpr(node.l, scope)) || truthy(evalExpr(node.r, scope));

  const a = evalExpr(node.l, scope);
  const b = evalExpr(node.r, scope);
  switch (op) {
    case '+': return Number(a) + Number(b);
    case '-': return Number(a) - Number(b);
    case '*': return Number(a) * Number(b);
    case '/': return Number(a) / Number(b);
    case '==': return eq(a, b);
    case '!=': return !eq(a, b);
    case '<': return cmp(a, b) < 0;
    case '>': return cmp(a, b) > 0;
    case '<=': return cmp(a, b) <= 0;
    case '>=': return cmp(a, b) >= 0;
    default: return null;
  }
}

function evalCall(node, scope) {
  const fn = node.fn;
  switch (fn) {
    case 'concat':
      return node.args.map((a) => strOf(evalExpr(a, scope))).join('');
    case 'if':
      return truthy(evalExpr(node.args[0], scope)) ? evalExpr(node.args[1], scope) : evalExpr(node.args[2], scope);
    case 'today':
      return localDate(new Date());
    case 'now': {
      const d = new Date();
      return `${localDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
    }
    case 'sum':
    case 'count':
    case 'avg':
    case 'min':
    case 'max':
      return aggregate(fn, aggValues(node.args[0], scope));
    default:
      // Unknown function (e.g. sql.scalar) is resolved by the runtime, not here.
      return null;
  }
}

// aggValues resolves an aggregate's argument to an array of values. The common
// form is `items.rows.Col` (a column across rows): map the trailing field over the
// array. A plain array arg is used as-is.
function aggValues(arg, scope) {
  if (arg && arg.k === 'member') {
    const base = evalExpr(arg.x, scope);
    if (Array.isArray(base)) return base.map((row) => (row == null ? null : row[arg.field]));
  }
  const v = evalExpr(arg, scope);
  return Array.isArray(v) ? v : [];
}

function aggregate(fn, vals) {
  const nums = vals.map(Number).filter((n) => !Number.isNaN(n));
  switch (fn) {
    case 'count': return vals.length;
    case 'sum': return nums.reduce((a, b) => a + b, 0);
    case 'avg': return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
    case 'min': return nums.length ? Math.min(...nums) : null;
    case 'max': return nums.length ? Math.max(...nums) : null;
    default: return null;
  }
}

// ── value helpers ────────────────────────────────────────────────────────────

// Local wall-clock date/time in MySQL DATE / DATETIME literal form. Not
// toISOString: that's UTC (yesterday before 07:00 in Jakarta) and its
// 'T…Z' form is rejected by a strict-mode DATETIME column.
const pad2 = (n) => String(n).padStart(2, '0');
function localDate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function isSet(v) {
  if (v == null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

function truthy(v) {
  if (Array.isArray(v)) return v.length > 0;
  return !!v;
}

function strOf(v) {
  return v == null ? '' : String(v);
}

// eq: numeric when both look numeric, else string equality (null ~ '').
function eq(a, b) {
  if (a == null && b == null) return true;
  if (numeric(a) && numeric(b)) return Number(a) === Number(b);
  return strOf(a) === strOf(b);
}

// cmp: numeric when both look numeric, else lexicographic. Returns -1/0/1.
function cmp(a, b) {
  if (numeric(a) && numeric(b)) {
    const x = Number(a), y = Number(b);
    return x < y ? -1 : x > y ? 1 : 0;
  }
  const x = strOf(a), y = strOf(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

function numeric(v) {
  return v !== '' && v != null && !Number.isNaN(Number(v));
}
